import { Injectable } from "@angular/core";
import {
  NavigationEnd,
  Route,
  Router,
  UrlSegment,
  UrlTree,
} from "@angular/router";
import { Store } from "@ngrx/store";
import { Observable, Subscription } from "rxjs";
import {
  filter,
  map,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from "rxjs/operators";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import { CurrentSessionData } from "src/model/class/general classes/current-session-data";
import { Plant } from "src/model/class/general classes/plant";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { iCredential } from "src/model/interface/icredential";
import { BIGService } from "src/service/big.service";
import { LoggerService } from "src/service/logger.service";
import { SecuretyDataProtectionService } from "src/service/securety-data-protection.service";
import { ThemeService } from "src/service/theme.service";
import { isHydrated } from "src/state/hydrate";
import { currentUserData } from "src/state/selectors/authentication.selectors";
import { listPlant, selectedPlant } from "src/state/selectors/plant.selectors";
import { AuthenticationService } from "../service/authentication.service";
import { EventService } from "../service/event.service";

@Injectable({
  providedIn: "root",
})

/**
 * La guardia {@link HomepageGuard} protegge la home dagli utenti autenticati:
 * Se l'utente è loggato viene rediretto sulla pagina di supervision
 * Se l'utente non è autenticato rimane su questa pagina e viene effettuato il controllo nel caso abbia precedentemente messo il rememberme, in tal caso parte la procedura di verifica token
 *
 */
export class HomepageGuard {
  private _logContext: string = "Homepage-guard";
  private _availablePlants: Plant[];
  private _previousUrl: string = "";
  private _currentUrl: string = "/";

  public _eventSubscriptions: Array<Subscription> = null;

  constructor(
    private _authService: AuthenticationService,
    private _eventService: EventService,
    private _router: Router,
    private _loggerService: LoggerService,
    private _store: Store,
    private _bigService: BIGService,
    private _securetyDataProtectionService: SecuretyDataProtectionService,
    private _themeService: ThemeService,
  ) {}

  canLoad(
    route: Route,
    segments: UrlSegment[],
  ):
    | boolean
    | UrlTree
    | Promise<boolean | UrlTree>
    | Observable<boolean | UrlTree> {
    return this.HomeActivationPageFlow();
  }

  /**
   * Home activation guard flow
   *
   * @returns
   */
  private HomeActivationPageFlow(): Observable<boolean | UrlTree> {
    this.getCurrentAndNextUrls();
    /** Reading App current user key from storage */
    return this.checkIsStoreReady().pipe(
      withLatestFrom(
        this._store.select(currentUserData),

        this._store.select(selectedPlant),

        this._store.select(listPlant),
      ),
      switchMap(async (combinedObject: Array<any>) => {
        const currentUser: CurrentSessionData = combinedObject[1]
          ?.currentUser as CurrentSessionData;

        const currentPlant = combinedObject[2];

        this._availablePlants = combinedObject[3];

        let canOpenUnLoggedPage: boolean | UrlTree = false;
        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          this._logContext,
          "HomeActivationPageFlow()",
          "Current user: " + JSON.stringify(currentUser),
        );

        if (currentUser) {
          const isRefreshTokenValid =
            this._authService.isRefreshTokenValid(currentUser);

          if (isRefreshTokenValid) {
            canOpenUnLoggedPage = false;
          } else if (currentUser.isAutoLoginOn) {
            canOpenUnLoggedPage = await this.tryForceLogin(
              currentUser,
              currentPlant,
            );
          } else {
            this._loggerService.log(
              LogLevel.LOG_ERROR,
              this._logContext,
              this._logContext,
              "HomeActivationPageFlow()",
              "currentUser with expired RefreshToken AND  without  'autoLogin'. Executing LOGOUT",
            );

            await this._authService.logout();

            canOpenUnLoggedPage = true;
          }
        } else {
          /** If current user key value is null remove all key from storage */
          this._authService.unsetCurrentUserData();

          // This check is required only if we are just entry the app, and there exist only one plant
          if (
            !this._previousUrl &&
            this._availablePlants &&
            this._availablePlants.length === 1 &&
            !this._availablePlants[0].isAutoLoginOn
          ) {
            this._eventService.OnSelectPlantShowLoginForm.next(
              this._availablePlants[0],
            );

            canOpenUnLoggedPage = this._router.createUrlTree([
              AppSettings.URI_PLANT_LIST_LOGIN,
            ]);
          } else {
            canOpenUnLoggedPage = true;
          }
        }

        return canOpenUnLoggedPage;
      }),
      tap((canOpenHomepage: boolean | UrlTree) => {
        if (!canOpenHomepage) {
          this.sendUserAuthorizedEvent();
        }
      }),
      map((canOpenHomepage: boolean | UrlTree) => {
        return this.canAccessUnProtectedRouteBasedONSpecificUrl(
          canOpenHomepage,
        );
      }),

      map((miscObject: UrlTree | boolean) => {
        /** Convert Promise<boolean> to boolean value */
        return miscObject;
      }),
    );
  }

  /**
   * Handle auto login flow.
   * No refresh token API call is fired beacuse, all token will be
   * refreshed from first 401 API executed
   *
   * @param currentUser Current user to autologin
   * @returns
   */
  private async tryForceLogin(
    currentUser: CurrentSessionData,
    currentPlant: Plant,
  ): Promise<boolean> {
    let canOpenUnLoggedPage: boolean = false;

    try {
      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "handleAutoLoginFlow()",
        "Handling AUTO-LOGIN FLOW",
      );
      //this._networkService.handleNotStandardMode(currentPlant.operativeMode);

      await this._authService.login(this.getUserObjectForLogin(currentUser));

      canOpenUnLoggedPage = false;
    } catch (exception) {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        this._logContext,
        "handleAutoLoginFlow()",
        "ERROR: " + exception.message,
      );
      canOpenUnLoggedPage = true;
    }
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "handleAutoLoginFlow()",
      "Can open Logged page: " + canOpenUnLoggedPage,
    );
    return canOpenUnLoggedPage;
  }

  /**
   * Get user object for credentials
   *
   * @param currentUser Current user object get from storage
   * @returns
   */
  private getUserObjectForLogin(currentUser: CurrentSessionData): iCredential {
    return {
      Username: this._securetyDataProtectionService.decypt(
        currentUser.username,
      ),
      //Password: currentUser.password,
      Password: this._securetyDataProtectionService.decypt(
        currentUser.password,
      ),
      IsSiboDevice: this._bigService.isMyToDevice,
      RememberMe: currentUser.rememberCredentials,
      isAutoLoginOn: true,
    };
  }

  /**
   * Send event from event service to propagate app initialization
   */
  private sendUserAuthorizedEvent(): void {
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "sendUserAuthorizedEvent()",
      "EMITTING ON OnLoginPlantDid",
    );
    this._eventService.OnLoginPlantDid.next(null);
  }

  /**
   *   This method check if the NgRx store has already been initialised (IE. hydrated) from the local storage.
   * It does so observing the value of the "hydrated" feature.
   * @returns An observable. This Observable shall not emit until the "hydrated" feature changes its value to TRUE.
   */
  private checkIsStoreReady(): Observable<boolean> {
    return this._store.select(isHydrated).pipe(
      tap((isHydrated: boolean) => {
        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          this._logContext,
          " checkIsStoreReady() ",
          "isHydrated: " + isHydrated,
        );
      }),
      filter((isHydrated: boolean) => isHydrated),
      tap((isHydrated: boolean) => {
        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          this._logContext,
          " checkIsStoreReady() ",
          "GO AHEAD!",
        );
      }),
      take(1),
    );
  }

  /**
   * Gets the preious and current urls
   */
  private getCurrentAndNextUrls(): void {
    const navigation = this._router.getCurrentNavigation();

    if (navigation) {
      this._currentUrl = navigation.extractedUrl.toString();
    }

    this._router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((previousUrl: NavigationEnd) => {
        this._previousUrl = previousUrl.url;
      });
  }

  /**
   * Check if we can navigate to a specific unprotected route based on specific page urls, that uses the Homepage guard
   * @param canOpenHomepage boolean or url tree, the login page url in this case
   * @returns boolean or a urlTree
   */
  private canAccessUnProtectedRouteBasedONSpecificUrl(
    canOpenHomepage: boolean | UrlTree,
  ): boolean | UrlTree {
    let canOpenUnLoggedPage: UrlTree | boolean = null;
    //When is trying to open homepage and current url is homepage it return only true
    //to avoid nested loop, otherwise return url
    if (typeof canOpenHomepage === "boolean" && canOpenHomepage === true) {
      canOpenUnLoggedPage = true;
    } else if (canOpenHomepage instanceof UrlTree) {
      switch (this._currentUrl) {
        case AppSettings.URI_PLANT_LIST_LOGIN:
        case AppSettings.URI_SINGLE_PLANT_LOGIN_GOBACK:
        case AppSettings.URI_SINGLE_PLANT_MANUAL:
        case AppSettings.URI_SINGLE_SELECT_PLANT_MANUAL:
        case AppSettings.URI_SINGLE_SELECT_DIRECT_PLANT:
          canOpenUnLoggedPage = true;
          break;
        default:
          canOpenUnLoggedPage = canOpenHomepage;
      }
    } else {
      canOpenUnLoggedPage = this._router.createUrlTree([
        AppSettings.URI_DIGITAL_PLACE_HOME_RESERVED_AREA,
      ]);
    }

    return canOpenUnLoggedPage;
  }
}
